
import { defineComponent } from 'vue';
import { Select, Spin, Input } from 'ant-design-vue';
import { CaretDownFilled } from '@ant-design/icons-vue';
import notification from 'ant-design-vue/lib/notification';
import { countries } from 'countries-list';
import sortBy from 'lodash/sortBy';

const { Option } = Select;
const { Password } = Input;

function capitalizeStringArray(stringArray:string[]) {
  return stringArray.map((word) => {
    const [first, ...rest] = word;
    const firstLetter = first.toUpperCase();
    return [firstLetter, ...rest].join('');
  });
}

export default defineComponent({
  name: 'SignUp',
  components: {
    Spin,
    Select,
    Input,
    Option,
    CaretDownFilled,
    Password,
  },

  data() {
    return {
      cleanMessages: {
        409.3: 'This account already exists.',
      },
      disabled: false,
      passwordFieldType: 'password',
      passwordVisibilityIconPath: '/eye-blocked.svg',
      showPassword: 'hide',
      hidePassword: 'show',
      rememberMe: false,
      countryArray: [...sortBy(Object.values(countries), 'name')],
      genderArray: ['Male', 'Female', 'Prefer not to say'] as string[],
      affiliationArray: ['International NGO', 'UN agency', 'Government', 'Academia', 'local NGO', 'Independent Consultant', 'Other'] as string[],
      capacityArray: ['National Staff', 'Expatriate Staff'] as string[],
      genderIndex: undefined as (number | undefined),
      capacityIndex: undefined as (number | undefined),
      countryIndex: undefined as (number | undefined),
      affiliationIndex: undefined as (number | undefined),
      form: {
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        gender: '',
        capacity: '',
        affiliation: '',
        organization: '',
        country: '',
      } as {[key:string]: string},
    };
  },
  computed: {

    /**
     * ensure the passwords are in sync
     */
    formValid():boolean {
      return this.containsNumber === 'success'
        && this.containsLetter === 'success'
        && this.containsSpecialCharacter === 'success'
        && this.containsCapital === 'success'
        && this.containsLowercase === 'success'
        && this.longEnough === 'success'
        && this.passwordFieldsMatch
        && this.form.capacity !== ''
        && this.form.capacity !== ''
        && this.form.gender !== ''
        && this.form.gender !== ''
        && this.form.affiliation !== ''
        && this.form.affiliation !== ''
        && this.form.organization !== ''
        && this.form.organization !== ''
        && this.form.country !== ''
        && this.form.country !== '';
    },
    containsCapital():string {
      return this.form.password.match(/[A-Z]/g) ? 'success' : 'error';
    },
    containsLowercase():string {
      return this.form.password.match(/[a-z]/g) ? 'success' : 'error';
    },
    containsNumber():string {
      return this.form.password.match(/[0-9]/g) ? 'success' : 'error';
    },
    containsLetter():string {
      return this.form.password.match(/[a-zA-Z]/g) ? 'success' : 'error';
    },
    containsSpecialCharacter():string {
      // eslint-disable-next-line no-useless-escape
      return this.form.password.match(/[\_\*\-?]/g) ? 'success' : 'error';
    },
    longEnough(): string {
      return this.form.password.length >= 8 ? 'success' : 'error';
    },
    passwordFieldsMatch(): boolean {
      return this.form.password === this.form.confirmPassword;
    },
    selectedGender():(string | undefined) {
      if (this.genderIndex || this.genderIndex === 0) {
        return this.genderArray[this.genderIndex];
      }
      return undefined;
    },
    selectedCapacity():(string | undefined) {
      if (this.capacityIndex || this.capacityIndex === 0) {
        return this.capacityArray[this.capacityIndex];
      }
      return undefined;
    },
    selectedAffiliation():(string | undefined) {
      if (this.affiliationIndex || this.affiliationIndex === 0) {
        return this.affiliationArray[this.affiliationIndex];
      }
      return undefined;
    },
    selectedCountry():(number | undefined) {
      if (this.countryIndex) {
        return this.countryIndex;
      }
      return undefined;
    },
    resetPasswordLocation() {
      return {
        path: '/reset-password',
        query: { ...this.$route.query },
      };
    },
    loginLocation() {
      return {
        path: '/login',
        query: { ...this.$route.query },
      };
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.disabled) {
      next(false);
    } else {
      next();
    }
  },
  methods: {
    setCountry(index:number) {
      this.countryIndex = index;
    },
    setCapacity(index:number) {
      this.capacityIndex = index;
    },
    setGender(index:number) {
      this.genderIndex = index;
    },
    setAffiliation(index:number) {
      this.affiliationIndex = index;
    },

    async submit() {
      // capture the drop-down list values
      this.form.capacity = this.capacityIndex != null ? this.capacityArray[this.capacityIndex] : '';
      this.form.affiliation = this.affiliationIndex != null ? this.affiliationArray[this.affiliationIndex] : '';
      this.form.country = String(this.countryIndex) || '';
      this.form.gender = this.genderIndex != null ? this.genderArray[this.genderIndex] : '';
      // don't submit if the passwords aren't valid & identical
      if (!this.formValid) {
        const missing = Object.keys(this.form).filter((key) => !this.form[key]);
        const missingFields = capitalizeStringArray(missing).join(', ');
        notification.error({
          message: `Please fill out all the required fields: ${missingFields}`,
        });
        return;
      }
      this.disabled = true;
      await this.$store.dispatch('user/createUserInfo', this.form).then(() => {
        this.$router.push('/login');
        notification.success({
          message: 'Your registration was successful. Please check your email to activate your new account.',
        });
      }).catch((e:string) => {
        notification.error({
          message: 'Something wrong happend!',
        });
      }).finally(() => {
        this.disabled = false;
      });
    },
  },
});
